<template>
  <div>
    <tx-action txType="Stake" />
    <div class="d-flex justify-center mt-16">
      <div class="deposit-box pa-10 p-relative">
        <c-tx-steps
          class="mt-6"
          borderType="stake"
          :options="steps"
          :current="currentStep"
          @setStep="s => (currentStep = s)"
        />
        <c-loading v-if="accountInfo.fetching || gasEstimating" />
        <div v-if="currentStep === 1" class="p-relative">
          <select-ecosystem :ecosystem="ecosystem" />
          <div class="d-flex justify-space-between align-start mt-16">
            <div class="flex-1">
              <select-asset
                :ecosystem="ecosystem"
                :selectedAsset="currentProject"
                class="mt-2"
              />
            </div>
            <enter-amount
              class="flex-1"
              v-model="form.amount"
              :maxAmount="maxAmountIncludingFee"
              :suffix="currentProject.staking.token"
            />
          </div>
          <div class="mt-10">
            <v-text-field
              v-model="form.memo"
              outlined
              single-line
              class="c-input search-stake"
              placeholder="Insert memo (optional)"
              hide-details="auto"
            ></v-text-field>
          </div>
          <div class="mt-10">
            <div>
              <c-btn
                class="width-100"
                @click="next"
                :disabled="btnNextDisabled"
                walletRequired
                >Continue</c-btn
              >
            </div>
            <div class="d-flex justify-center mt-4">
              <v-checkbox
                v-if="!!currentProject.staking.msgsMixed"
                v-model="form.shouldWithdrawRewards"
                label="Claim all rewards and stake"
                class="mr-5"
              ></v-checkbox>
            </div>
          </div>
        </div>
        <div v-if="currentStep === 2" class="mt-10">
          <gas-option :gas="defaultGas" v-model="gasOption">
            <div class="d-flex mt-10">
              <div>
                <div class="text-5 font-weight-medium">Stake Amount</div>
                <div class="d-flex align-center mt-2 text-2">
                  <div>
                    <span class="primary--text">{{ form.amount }}</span>
                    <span class="gray--text text-uppercase ml-1">{{
                      currentProject.staking.token
                    }}</span>
                  </div>
                  <img
                    v-if="$vuetify.theme.dark"
                    :src="
                      currentProject.logoSmallDark || currentProject.logoSmall
                    "
                    height="36"
                    width="36"
                    class="ml-4"
                  />
                  <img
                    v-else
                    :src="currentProject.logoSmall"
                    height="36"
                    width="36"
                    class="ml-4"
                  />
                </div>
                <div class="mt-2 gray--text text-5">
                  ${{ amountUsd | currency }}
                </div>
              </div>
              <div class="ml-10">
                <div class="text-5 font-weight-medium ml-10">
                  Transaction Fee
                </div>
                <div class="d-flex align-center mt-4 text-2">
                  <v-divider vertical class="mr-10 border-vault"></v-divider>
                  <div>
                    <span class="primary--text">{{
                      gasOption.gasPrice | denomFormat
                    }}</span>
                    <span class="gray--text text-uppercase ml-1">{{
                      currentProject.staking.token
                    }}</span>
                  </div>
                </div>
                <div class="mt-2 gray--text text-5 ml-10">
                  ${{ gasPriceUsd | currency }}
                </div>
              </div>
            </div>
            <div class="mt-6 text-5">
              <span class="font-weight-medium">Memo:</span>
              <span class="darkgray--text ml-4">{{ form.memo }}</span>
            </div>
          </gas-option>
          <div class="mt-16">
            <c-btn
              class="width-100"
              @click="confirm"
              :disabled="btnStakeDisabled"
              >Confirm</c-btn
            >
          </div>
        </div>
      </div>
    </div>
    <tx-tendermint
      v-if="showTxDialog"
      name="deposit"
      title="Stake"
      successMesssage="Transaction sent"
      :getRawTx="getRawTx"
      :metadata="{ amount: form.amount }"
      :gasOption="gasOption"
      @cancel="showTxDialog = false"
      @onSuccess="onTxSuccess"
    >
    </tx-tendermint>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BN from "bignumber.js";
import TxAction from "@/views/stake/components/tx/TxAction.vue";
import EnterAmount from "@/views/stake/components/tx/EnterAmount.vue";
import GasOption from "../components/GasOption.vue";
import SelectEcosystem from "@/views/stake/components/tx/SelectEcosystem.vue";
import SelectAsset from "@/views/stake/components/tx/SelectAsset.vue";

export default {
  components: {
    TxAction,
    EnterAmount,
    GasOption,
    SelectEcosystem,
    SelectAsset,
  },
  computed: {
    ...mapGetters({
      currentProject: "projectConfig/latestProject",
      getTokenPriceInUsd: "appConfig/getTokenPriceInUsd",
      accountInfo: "staking/tendermint/accountInfo",
      rewards: "staking/tendermint/rewards",
    }),
    availableBalance() {
      return new BN(this.accountInfo?.result?.total ?? 0)
        .dividedBy(this.currentProject.staking.conversionRate)
        .toString();
    },
    balanceUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.availableBalance
      );
    },
    totalRewards() {
      return new BN(this.rewards.result?.total ?? 0)
        .dividedBy(this.currentProject.staking.conversionRate)
        .toString();
    },
    defaultGas() {
      if (new BN(this.gasEstimated).gt(0)) return this.gasEstimated;
      let gas = new BN(this.currentProject.staking.gas.Stake);
      if (this.form.shouldWithdrawRewards) {
        gas = gas.plus(
          new BN(this.currentProject.staking.gas.ClaimRewards).multipliedBy(
            this.rewards.result?.balances.length
          )
        );
      }
      return gas.toNumber();
    },
    defaultGasPrice() {
      if (new BN(this.gasOption.gasPrice).gt(0)) {
        return Number(this.gasOption.gasPrice);
      }
      return new BN(this.currentProject.staking.gasPriceStep.medium)
        .multipliedBy(this.defaultGas)
        .div(this.currentProject.staking.conversionRate)
        .toNumber();
    },
    gasPriceUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.gasOption.gasPrice
      );
    },
    maxAmount() {
      return new BN(this.availableBalance)
        .plus(this.form.shouldWithdrawRewards ? this.totalRewards : 0)
        .toNumber();
    },
    maxAmountIncludingFee() {
      return Math.max(
        new BN(this.maxAmount)
          .multipliedBy(
            this.gasOption.gasPrice > 0 && this.form.shouldWithdrawRewards
              ? 0.95
              : 1
          )
          .minus(this.defaultGasPrice)
          .toNumber(),
        0
      );
    },
    isSufficentAmount() {
      return (
        new BN(this.form.amount).isGreaterThan(0) &&
        new BN(this.form.amount).isLessThanOrEqualTo(this.maxAmountIncludingFee)
      );
    },
    amountUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.form.amount
      );
    },
    btnNextDisabled() {
      if (!this.currentProject.status?.active) return true;
      return !this.isSufficentAmount;
    },
    btnStakeDisabled() {
      return this.gasOption.gas <= 0 || this.gasOption.gasPrice <= 0;
    },
  },
  data: () => ({
    ecosystem: "Tendermint",
    steps: [
      {
        step: 1,
        text: "Amount",
      },
      {
        step: 2,
        text: "Gas option",
      },
    ],
    currentStep: 1,
    form: {
      amount: 0,
      memo: "",
      shouldWithdrawRewards: false,
    },
    gasOption: {
      gas: 0,
      gasPrice: 0,
    },
    showTxDialog: false,
    gasEstimated: 0,
    gasEstimating: false,
  }),
  methods: {
    async next() {
      this.currentStep = 2;
      this.gasEstimating = true;
      await this.updateGasEstimated();
      this.gasEstimating = false;
    },
    confirm() {
      this.showTxDialog = true;
    },
    async getRawTx() {
      return this.$store.dispatch("staking/tendermint/getStakeRawTx", {
        amount: this.form.amount,
        memo: this.form.memo,
        shouldWithdrawRewards: this.form.shouldWithdrawRewards,
        gas: this.gasOption.gas,
        gasPrice: new BN(this.gasOption.gasPrice)
          .multipliedBy(this.currentProject.staking.conversionRate)
          .toNumber(),
      });
    },
    onTxSuccess() {
      this.showTxDialog = false;
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
      });
      this.$emit("close");
    },
    async updateGasEstimated() {
      const rawTx = await this.getRawTx();
      const { rawTxUpdated } = await this.$store.dispatch(
        "staking/tendermint/simulateTx",
        {
          rawTx,
        }
      );
      if (this.gasOption.gas > 0) {
        this.gasEstimated = Number(rawTxUpdated.fee.gas);
      }
    },
  },
  watch: {
    currentStep(value) {
      if (value === 1) {
        this.gasOption = {
          gas: 0,
          gasPrice: 0,
        };
        this.gasEstimated = 0;
      }
    },
    ["currentProject.config.network"]() {
      this.currentStep = 1;
      this.form.amount = 0;
    },
  },
};
</script>
